import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Check, FileBadge2, Link2, ScanSearch, Sigma } from "lucide-react";
import type { AiUseRecord, CheckedFigure, FigureCheck, FigureStatus } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { Badge, cn, Popover, PopoverContent, PopoverTrigger } from "@ncp/ui";
import { isFigureIssue, type CheckedFigureItem, type FigureCheckStatus } from "./reports";

const statusVariant: Record<FigureStatus, "success" | "outline" | "warning" | "danger"> = {
  Verified: "success", Rounded: "outline", Unsupported: "warning", Mismatch: "danger", Stale: "danger", PrecedenceBreach: "danger",
};
const statusRank: Record<FigureStatus, number> = { Mismatch: 0, PrecedenceBreach: 1, Stale: 2, Unsupported: 3, Rounded: 4, Verified: 5 };
const issue = (f: CheckedFigure) => f.status !== "Verified" && f.status !== "Rounded";

/**
 * The figure check of an economic draft: every figure traced to the statistics warehouse, a tool or a cited source. Problems come first
 * (a mismatch with the cited source, a superseded release, an international figure quoted as the UAE figure, a figure nothing supports);
 * the traced figures are one click away so the reviewer can see what each one rests on.
 */
export function FigureCheckPanel({ check, className }: { check: FigureCheck; className?: string }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const sorted = [...check.figures].sort((a, b) => statusRank[a.status] - statusRank[b.status]);
  const problems = sorted.filter(issue);
  const shown = all ? sorted : problems.slice(0, 6);
  const penalty = Math.round(check.penalty * 100);
  return (
    <div className={cn("rounded-xl border border-sand-200 bg-white p-3", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-aeblack-900"><Sigma className="size-3.5 text-muted-foreground" /> {t("ai.figureCheck.title")}</p>
        <Badge variant={problems.length === 0 ? "success" : problems.some((f) => f.status !== "Unsupported") ? "danger" : "warning"} size="sm">
          {t("ai.figureCheck.traced", { traced: check.traced, total: check.total })}
        </Badge>
        {penalty > 0 ? <span className="text-xs text-muted-foreground">{t("ai.figureCheck.penalty", { points: penalty })}</span> : null}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{t("ai.figureCheck.hint")}</p>
      {shown.length ? (
        <ul className="mt-2 space-y-1.5">
          {shown.map((f, i) => (
            <li key={`${f.text}-${f.status}-${i}`} className="rounded-lg border border-sand-200 bg-sand-50/50 p-2 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold tabular" dir="ltr">{f.text}</span>
                <Badge variant={statusVariant[f.status]} size="sm">{t(`ai.figureCheck.status.${f.status}`)}</Badge>
                {f.expected && issue(f) ? <span className="text-muted-foreground">{t("ai.figureCheck.expected", { value: f.expected })}</span> : null}
              </div>
              {f.matchedTo ? <p className="mt-1 text-muted-foreground">{t("ai.figureCheck.matchedTo")}: {f.matchedTo}</p> : null}
              {f.note && issue(f) ? <p className="mt-1">{f.note}</p> : null}
              <p className="mt-1 line-clamp-2 text-xs italic text-muted-foreground" dir="auto">“{f.sentence}”</p>
            </li>
          ))}
        </ul>
      ) : <p className="mt-2 text-xs text-success-700">{t("ai.figureCheck.allTraced")}</p>}
      {check.figures.length > shown.length || all ? (
        <button type="button" onClick={() => setAll(!all)} className="mt-2 text-xs font-medium text-foreground underline-offset-2 hover:underline">
          {all ? t("ai.figureCheck.showProblems") : t("ai.figureCheck.showAll", { count: check.figures.length })}
        </button>
      ) : null}
    </div>
  );
}

/** The AI-use record of a deliverable: agent and prompt version (never the model's name), the tools and sources it used, what was checked. */
export function AiUsePanel({ record, className }: { record: AiUseRecord; className?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className={cn("rounded-xl border border-sand-200 bg-white p-3 text-xs", className)}>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-1.5 text-start font-semibold text-aeblack-900" aria-expanded={open}>
        <FileBadge2 className="size-3.5 text-muted-foreground" /> {t("ai.aiUse.title")}
        <span className="ms-2 font-normal text-muted-foreground">{record.agentName}{record.promptVersion ? ` · ${t("ai.aiUse.prompt")} ${record.promptVersion.slice(0, 8)}` : ""}</span>
      </button>
      {open ? (
        <dl className="mt-2 grid gap-x-4 gap-y-1.5 sm:grid-cols-[10rem_1fr]">
          <dt className="text-muted-foreground">{t("ai.aiUse.generated")}</dt><dd>{new Date(record.generatedAt).toLocaleString()}{record.trigger ? ` · ${record.trigger}` : ""}</dd>
          <dt className="text-muted-foreground">{t("ai.aiUse.tools")}</dt><dd className="font-mono">{record.tools.length ? record.tools.join(", ") : "—"}</dd>
          <dt className="text-muted-foreground">{t("ai.aiUse.sources")}</dt>
          <dd><ol className="list-inside list-decimal space-y-0.5">{record.sources.slice(0, 12).map((s) => <li key={s.index} dir="auto">{s.title}{s.locator ? <span className="text-muted-foreground"> – {s.locator}</span> : null}</li>)}</ol>{record.sources.length > 12 ? <span className="text-muted-foreground">{t("ai.aiUse.more", { count: record.sources.length - 12 })}</span> : null}</dd>
          <dt className="text-muted-foreground">{t("ai.aiUse.checks")}</dt>
          <dd>{[record.checks.figures, record.checks.citations ? t("ai.aiUse.citations", { value: record.checks.citations }) : null, record.checks.quality != null ? t("ai.aiUse.quality", { value: record.checks.quality }) : null, record.checks.guardrails.length ? record.checks.guardrails.join(", ") : null].filter(Boolean).join(" · ") || "—"}</dd>
          {record.checks.unsourced?.length ? (<><dt className="text-muted-foreground">{t("ai.aiUse.unsourced")}</dt><dd><ul className="list-inside list-disc space-y-0.5">{record.checks.unsourced.map((s) => <li key={s} dir="auto">{s}</li>)}</ul></dd></>) : null}
          <dt className="text-muted-foreground">{t("ai.aiUse.review")}</dt><dd>{t("ai.aiUse.reviewText")}</dd>
        </dl>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- ECONIC: the inline figure check (APPLE-SPEC §7 10.18; DESIGN-SYSTEM §10.18; C25)
//
// The popover of `checks.figures`: 320 px in the menu material (10 px radius, hairline arrow), anchored under the highlighted figure and
// never over the sentence it checks (Radix places it below and flips it only when there is no room). "Figure check" with the status as
// text ("● Mismatch"), one sentence saying what the source holds (the figure in bold), the source line, then the action group with the
// default last: Source (plain) · Keep 5.2 % (secondary) · Use 5.5 % (primary). The checked figure in the prose carries an orange highlighter.
// Used by the report studio (WP-PDF-UI) and any ECONIC screen that shows checked prose. Styles: `.rp-fig*` in
// apps/assistant/src/econic/reports/reports.css (imported by `econic/reports`). Strings: `assistant:econic.reports.figure.*`.

/** Figures and period codes inside a sentence keep their left-to-right order in Arabic: each run is isolated on its own (never a phrase). */
const FIGURE_RUN = /(?:[+\-−×]\s?)?\d[\d,]*(?:\.\d+)?\s?%|[+−]\d[\d,]*(?:\.\d+)?|\d{4}-(?:\d{2}|Q\d|W\d{2})\b/g;

/** Status as text (APPLE-SPEC §7 10.10): the kind of the 6 px dot and the coloured word. */
export type FigureStatusKind = "good" | "warn" | "serious" | "info" | "plain";
const FIGURE_KIND: Record<FigureCheckStatus, FigureStatusKind> = {
  verified: "good", rounded: "good", mismatch: "serious", arithmetic: "serious", inconsistent: "warn", stale: "warn", precedence: "warn", unsourced: "plain", forecast: "info",
};
export const figureStatusKind = (s: FigureCheckStatus): FigureStatusKind => FIGURE_KIND[s] ?? "plain";

const norm = (s: string) => s.replace(/\s+/g, "").replace("−", "-");

/** A sentence with each figure isolated; the figure equal to `bold` (the source's value) is set in bold. */
function FigureSentence({ text, bold }: { text: string; bold?: string | null }) {
  const parts: ReactNode[] = [];
  let at = 0;
  let strong = false;
  for (const m of text.matchAll(FIGURE_RUN)) {
    const i = m.index ?? 0;
    if (i > at) parts.push(<Fragment key={`t${i}`}>{text.slice(at, i)}</Fragment>);
    const isBold = !strong && !!bold && norm(m[0]) === norm(bold);
    if (isBold) strong = true;
    parts.push(isBold ? <b key={`f${i}`}><bdi dir="ltr">{m[0]}</bdi></b> : <bdi key={`f${i}`} dir="ltr">{m[0]}</bdi>);
    at = i + m[0].length;
  }
  if (at < text.length) parts.push(<Fragment key="end">{text.slice(at)}</Fragment>);
  return <>{parts}</>;
}

export interface FigureCheckActions {
  /** Replace the stated figure with what the source says (the caller writes it: a `set_text` op, a suggestion). */
  onUse?: (figure: CheckedFigureItem) => void;
  /** Keep the stated figure (the caller records or simply dismisses the flag). */
  onKeep?: (figure: CheckedFigureItem) => void;
  /** Open the source the figure was checked against. */
  onSource?: (figure: CheckedFigureItem) => void;
  /** Disable Use/Keep (a reader without edit rights; a call in flight). */
  readOnly?: boolean;
  busy?: boolean;
}

/** The card itself (also usable inline on narrow widths, where it sits in the flow under the sentence). */
export function FigureCheckCard({ figure, lang, onUse, onKeep, onSource, readOnly, busy, className }: FigureCheckActions & { figure: CheckedFigureItem; lang: "en" | "ar"; className?: string }) {
  const { t } = useTranslation();
  const kind = figureStatusKind(figure.status);
  const issue = isFigureIssue(figure.status);
  // The API's sentence ("FCSC … gives 2.2% for 2025-12; the text states 2.8%."); without one, what the source says beside the text.
  const sentence = ((lang === "ar" && figure.noteAr) || figure.note)
    || (figure.expected
      ? t(figure.period ? "assistant:econic.reports.figure.says" : "assistant:econic.reports.figure.saysPlain", { expected: figure.expected, period: figure.period, stated: figure.stated })
      : "");
  const evidence = figure.evidence.filter((e) => e.label || e.value).slice(0, 2);
  const canUse = !!figure.expected && issue && !readOnly;
  const canKeep = issue && !readOnly;
  const canSource = !!onSource && figure.evidence.length > 0;
  return (
    <div className={cn("rp-figcard", className)} data-status={figure.status}>
      <div className="rp-figcard-h">
        <ScanSearch aria-hidden className="ic" />
        <span className="ttl">{t("assistant:econic.reports.figure.title")}</span>
        <span className={cn("rp-s", kind)}>{t(`assistant:econic.reports.figure.status.${figure.status}`)}</span>
      </div>
      {sentence ? <p className="rp-figcard-n" dir="auto"><FigureSentence text={sentence} bold={figure.expected ?? (issue ? null : figure.stated)} /></p> : null}
      {evidence.length ? (
        <ul className="rp-figcard-e">
          {evidence.map((e, i) => (
            <li key={`${e.ref ?? e.label}-${i}`}>
              <span className="k">{t(`assistant:econic.reports.figure.evidence.${e.kind === "passage" || e.kind === "calc" ? e.kind : "series"}`)}</span>
              <span className="l" dir="auto"><FigureSentence text={e.label} />{e.value ? <> · <bdi dir="ltr" className="v">{e.value}</bdi></> : null}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {(canSource || (onKeep && canKeep) || (onUse && canUse)) ? (
        <div className="rp-figcard-a">
          {canSource ? (
            <button type="button" className="ec-btn plain sm src" onClick={() => onSource!(figure)}>
              <Link2 aria-hidden />{t("assistant:econic.reports.figure.openSource")}
            </button>
          ) : null}
          {onKeep && canKeep ? (
            <button type="button" className="ec-btn secondary sm" disabled={busy} onClick={() => onKeep(figure)}>
              {t("assistant:econic.reports.figure.keep", { value: `⁦${figure.stated}⁩` })}
            </button>
          ) : null}
          {onUse && canUse ? (
            <button type="button" className="ec-btn primary sm" disabled={busy} onClick={() => onUse(figure)}>
              {t("assistant:econic.reports.figure.use", { value: `⁦${figure.expected}⁩` })}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** One highlighted figure with its popover. */
export function FigureMark({ figure, lang, children, ...actions }: FigureCheckActions & { figure: CheckedFigureItem; lang: "en" | "ar"; children: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const issue = isFigureIssue(figure.status);
  const close = <T,>(fn?: (f: CheckedFigureItem) => T) => (fn ? (f: CheckedFigureItem) => { setOpen(false); fn(f); } : undefined);
  // The hairline arrow points at the figure even when the popover is shifted to stay on screen (Radix moves the card, not the arrow).
  const trigger = useRef<HTMLButtonElement | null>(null);
  const card = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    let n = 0;
    let raf = 0;
    const place = () => {
      const a = trigger.current?.getBoundingClientRect();
      const b = card.current?.getBoundingClientRect();
      if (a && b && b.width) card.current!.style.setProperty("--rp-arrow-x", `${Math.round(Math.min(b.width - 20, Math.max(20, a.left + a.width / 2 - b.left)))}px`);
      if (++n < 12) raf = requestAnimationFrame(place);
    };
    raf = requestAnimationFrame(place);
    return () => cancelAnimationFrame(raf);
  }, [open]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={trigger}
          type="button"
          className={cn("rp-fig", issue ? "issue" : "ok")}
          data-status={figure.status}
          aria-label={t("assistant:econic.reports.figure.markLabel", { value: figure.stated, status: t(`assistant:econic.reports.figure.status.${figure.status}`) })}
        >
          {children}
          {!issue && (figure.status === "verified" || figure.status === "rounded") ? <Check aria-hidden className="ok-ic" /> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent ref={card} side="bottom" align="center" sideOffset={9} collisionPadding={12} className="rp-figpop" dir={lang === "ar" ? "rtl" : "ltr"}>
        <FigureCheckCard figure={figure} lang={lang} {...actions} onUse={close(actions.onUse)} onKeep={close(actions.onKeep)} onSource={close(actions.onSource)} />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Prose with its checked figures marked: figures that do not hold (mismatch, arithmetic, inconsistent, stale, precedence) get the orange
 * highlighter and open the popover; verified figures get a quiet check only when `showVerified`. Offsets are those `checks.figures`
 * returned for exactly this `text`.
 */
export function FigureCheckedText({ text, figures, lang, showVerified = false, hidden, renderText, ...actions }: FigureCheckActions & {
  text: string;
  figures: CheckedFigureItem[];
  lang: "en" | "ar";
  showVerified?: boolean;
  /** Figures the person kept (by start offset): drawn plain. */
  hidden?: ReadonlySet<number>;
  /** Draws the text between marked figures (e.g. isolating left-to-right runs inside Arabic); plain text by default. */
  renderText?: (segment: string) => ReactNode;
}) {
  const plain = renderText ?? ((x: string) => x);
  const marks = figures
    .filter((f) => (isFigureIssue(f.status) || (showVerified && (f.status === "verified" || f.status === "rounded"))) && !hidden?.has(f.start))
    .filter((f) => f.start >= 0 && f.start + f.length <= text.length)
    .sort((a, b) => a.start - b.start)
    .filter((f, i, arr) => i === 0 || f.start >= arr[i - 1]!.start + arr[i - 1]!.length);
  const parts: ReactNode[] = [];
  let at = 0;
  marks.forEach((f, i) => {
    if (f.start > at) parts.push(<Fragment key={`t${i}`}>{plain(text.slice(at, f.start))}</Fragment>);
    parts.push(
      <FigureMark key={`f${f.start}`} figure={f} lang={lang} {...actions}>
        <bdi dir="ltr">{text.slice(f.start, f.start + f.length)}</bdi>
      </FigureMark>,
    );
    at = f.start + f.length;
  });
  if (at < text.length) parts.push(<Fragment key="end">{plain(text.slice(at))}</Fragment>);
  return <>{parts}</>;
}

/** The counts of a figure check as a status line ("● 1 to look at  ● 19 verified  ● 6 without a source"). */
export function FigureCheckCounts({ counts, className }: { counts: Partial<Record<FigureCheckStatus, number>>; className?: string }) {
  const { t } = useTranslation();
  const issues = (["mismatch", "arithmetic", "inconsistent", "stale", "precedence"] as const).reduce((n, k) => n + (counts[k] ?? 0), 0);
  const verified = (counts.verified ?? 0) + (counts.rounded ?? 0);
  const parts = [
    issues ? { kind: "serious", text: t("assistant:econic.reports.figure.countIssues", { count: issues }) } : null,
    verified ? { kind: "good", text: t("assistant:econic.reports.figure.countVerified", { count: verified }) } : null,
    counts.forecast ? { kind: "info", text: t("assistant:econic.reports.figure.countForecast", { count: counts.forecast }) } : null,
    counts.unsourced ? { kind: "plain", text: t("assistant:econic.reports.figure.countUnsourced", { count: counts.unsourced }) } : null,
  ].filter((x): x is { kind: string; text: string } => !!x);
  if (!parts.length) return null;
  return (
    <span className={cn("rp-stline rp-figcounts", className)}>
      {parts.map((p) => <span key={p.text} className={cn("rp-s", p.kind)}>{p.text}</span>)}
    </span>
  );
}
